import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, CheckCircle2, XCircle, RefreshCw, Unplug, ExternalLink } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const API = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8001") + "/api";
const EMERGENT_AUTH_URL = "https://auth.emergentagent.com";

const EmailSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [syncingGmail, setSyncingGmail] = useState(false);
  const [syncingOutlook, setSyncingOutlook] = useState(false);
  const [disconnectingGmail, setDisconnectingGmail] = useState(false);
  const [disconnectingOutlook, setDisconnectingOutlook] = useState(false);

  useEffect(() => {
    fetchEmailAccounts();
    
    // Check for OAuth callbacks
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    const outlookConnected = urlParams.get("outlook_connected");
    
    if (sessionId) {
      handleGmailCallback(sessionId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (outlookConnected) {
      toast.success("Outlook connected successfully!");
      fetchEmailAccounts();
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchEmailAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/email-sync/accounts`, {
        withCredentials: true
      });
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error("Failed to fetch email accounts:", error);
      toast.error("Failed to load email accounts");
    }
    setLoading(false);
  };

  const handleGmailCallback = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/email-sync/gmail/callback`,
        null,
        {
          params: { session_id: sessionId },
          withCredentials: true
        }
      );
      
      if (response.data.success) {
        toast.success(`Gmail connected: ${response.data.email}`);
        fetchEmailAccounts();
      }
    } catch (error) {
      console.error("Gmail callback error:", error);
      toast.error(error.response?.data?.detail || "Failed to connect Gmail");
    }
  };

  const connectGmail = () => {
    const redirectUrl = encodeURIComponent(window.location.href);
    const authUrl = `${EMERGENT_AUTH_URL}/?redirect=${redirectUrl}`;
    window.location.href = authUrl;
  };

  const syncGmail = async () => {
    setSyncingGmail(true);
    try {
      const response = await axios.post(
        `${API}/email-sync/gmail/sync`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchEmailAccounts();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Gmail sync error:", error);
      toast.error(error.response?.data?.detail || "Failed to sync Gmail");
    }
    setSyncingGmail(false);
  };

  const disconnectGmail = async () => {
    if (!window.confirm("Are you sure you want to disconnect your Gmail account?")) {
      return;
    }

    setDisconnectingGmail(true);
    try {
      const response = await axios.delete(`${API}/email-sync/gmail/disconnect`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        toast.success("Gmail disconnected successfully");
        fetchEmailAccounts();
      }
    } catch (error) {
      console.error("Gmail disconnect error:", error);
      toast.error(error.response?.data?.detail || "Failed to disconnect Gmail");
    }
    setDisconnectingGmail(false);
  };

  // Outlook Methods
  const connectOutlook = async () => {
    try {
      const response = await axios.get(`${API}/email-sync/outlook/auth-url`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        window.location.href = response.data.auth_url;
      } else {
        toast.error("Failed to get Outlook authorization URL");
      }
    } catch (error) {
      console.error("Outlook auth URL error:", error);
      toast.error(error.response?.data?.detail || "Failed to connect Outlook");
    }
  };

  const syncOutlook = async () => {
    setSyncingOutlook(true);
    try {
      const response = await axios.post(
        `${API}/email-sync/outlook/sync`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchEmailAccounts();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error("Outlook sync error:", error);
      toast.error(error.response?.data?.detail || "Failed to sync Outlook");
    }
    setSyncingOutlook(false);
  };

  const disconnectOutlook = async () => {
    if (!window.confirm("Are you sure you want to disconnect your Outlook account?")) {
      return;
    }

    setDisconnectingOutlook(true);
    try {
      const response = await axios.delete(`${API}/email-sync/outlook/disconnect`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        toast.success("Outlook disconnected successfully");
        fetchEmailAccounts();
      }
    } catch (error) {
      console.error("Outlook disconnect error:", error);
      toast.error(error.response?.data?.detail || "Failed to disconnect Outlook");
    }
    setDisconnectingOutlook(false);
  };

  const gmailAccount = accounts.find(acc => acc.provider === "gmail" && acc.is_active);
  const outlookAccount = accounts.find(acc => acc.provider === "outlook" && acc.is_active);
  const isGmailConnected = !!gmailAccount;
  const isOutlookConnected = !!outlookAccount;

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Email Integration</h1>
        <p className="text-slate-600 mt-2">
          Connect your email accounts to sync emails with your CRM leads
        </p>
      </div>

      {/* Gmail Integration Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Mail className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <CardTitle>Gmail</CardTitle>
                <CardDescription>Connect your Gmail account to sync emails</CardDescription>
              </div>
            </div>
            {isGmailConnected ? (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Connected
              </Badge>
            ) : (
              <Badge variant="outline" className="text-slate-500">
                <XCircle className="w-3 h-3 mr-1" />
                Not Connected
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isGmailConnected ? (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{gmailAccount.email}</p>
                    <p className="text-sm text-slate-600">{gmailAccount.name}</p>
                  </div>
                  {gmailAccount.picture && (
                    <img 
                      src={gmailAccount.picture} 
                      alt="Profile" 
                      className="w-10 h-10 rounded-full"
                    />
                  )}
                </div>
                {gmailAccount.last_sync_at && (
                  <p className="text-xs text-slate-500 mt-2">
                    Last synced: {new Date(gmailAccount.last_sync_at).toLocaleString()}
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={syncGmail} 
                  disabled={syncingGmail}
                  className="flex-1"
                >
                  {syncingGmail ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sync Now
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={disconnectGmail}
                  disabled={disconnectingGmail}
                >
                  {disconnectingGmail ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Disconnecting...
                    </>
                  ) : (
                    <>
                      <Unplug className="w-4 h-4 mr-2" />
                      Disconnect
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert>
                <AlertDescription>
                  Connect your Gmail account to automatically sync emails with your CRM. 
                  Emails from leads will be logged and displayed in their profiles.
                </AlertDescription>
              </Alert>
              <Button onClick={connectGmail} className="w-full">
                <Mail className="w-4 h-4 mr-2" />
                Connect Gmail Account
              </Button>
              <div className="text-xs text-slate-500 space-y-1">
                <p>✓ Secure OAuth2 authentication via Emergent</p>
                <p>✓ No passwords stored</p>
                <p>✓ Read-only access to your emails</p>
                <p>✓ You can disconnect anytime</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Outlook Integration Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <CardTitle>Microsoft Outlook</CardTitle>
                <CardDescription>Connect your Outlook account to sync emails</CardDescription>
              </div>
            </div>
            {isOutlookConnected ? (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Connected
              </Badge>
            ) : (
              <Badge variant="outline" className="text-slate-500">
                <XCircle className="w-3 h-3 mr-1" />
                Not Connected
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isOutlookConnected ? (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{outlookAccount.email}</p>
                    <p className="text-sm text-slate-600">{outlookAccount.name}</p>
                  </div>
                </div>
                {outlookAccount.last_sync_at && (
                  <p className="text-xs text-slate-500 mt-2">
                    Last synced: {new Date(outlookAccount.last_sync_at).toLocaleString()}
                  </p>
                )}
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={syncOutlook} 
                  disabled={syncingOutlook}
                  className="flex-1"
                >
                  {syncingOutlook ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Sync Now
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={disconnectOutlook}
                  disabled={disconnectingOutlook}
                >
                  {disconnectingOutlook ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Disconnecting...
                    </>
                  ) : (
                    <>
                      <Unplug className="w-4 h-4 mr-2" />
                      Disconnect
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Alert>
                <AlertDescription>
                  Connect your Microsoft Outlook/Microsoft 365 account to automatically sync emails with your CRM. 
                  Emails from leads will be logged and displayed in their profiles.
                </AlertDescription>
              </Alert>
              <Button onClick={connectOutlook} className="w-full">
                <Mail className="w-4 h-4 mr-2" />
                Connect Outlook Account
              </Button>
              <div className="text-xs text-slate-500 space-y-1">
                <p>✓ Secure OAuth2 authentication via Microsoft</p>
                <p>✓ No passwords stored</p>
                <p>✓ Read-only access to your emails</p>
                <p>✓ You can disconnect anytime</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Help Section */}
      <div className="mt-6 p-4 bg-slate-50 rounded-lg">
        <h3 className="font-medium text-slate-900 mb-2">How Email Sync Works</h3>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>• Connect your Gmail or Outlook account using secure OAuth2 authentication</li>
          <li>• Click "Sync Now" to fetch recent emails from your inbox</li>
          <li>• Emails are automatically matched with leads in your CRM</li>
          <li>• View synced emails directly in lead profiles</li>
          <li>• Email sync helps track all communications in one place</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailSettingsPage;
